import { useState, useEffect } from 'react';
import { Container, Header, Content, Navbar, Table, Button, Modal, ButtonToolbar, ButtonGroup, Badge, Input, InputGroup, Panel, Message, IconButton, Tooltip, Whisper, Checkbox, useToaster } from 'rsuite';
import { Search, Trash, Page, WarningRound, Edit as EditIcon, BarChart, FileDownload } from '@rsuite/icons';
import { getApiUrl, getAuthHeaders, getUserId } from './utils/apiConfig';
import { useAuth } from './utils/useAuth';
import { formatUptime } from './utils/dateUtils';
import DateDisplay from './components/DateDisplay';
import LogDialog from './components/LogDialog';
import MetricsDialog from './components/MetricsDialog';
import JobDetailsDialog from './components/JobDetailsDialog';
import CopyButton from './components/CopyButton';

const { Column, HeaderCell, Cell } = Table;

const getJobStatusBadge = (job) => {
  const status = job.status;
  if (status === 'completed') {
    return <Badge content="Completed" color="green" />;
  } else if (status === 'running') {
    const badge = <Badge content="Running" color="blue" />;
    if (job.stage) {
      return (
        <Whisper
          placement="topStart"
          trigger="hover"
          speaker={<Tooltip>{job.stage}</Tooltip>}
        >
          <span style={{ display: 'inline-block', cursor: 'default' }}>{badge}</span>
        </Whisper>
      );
    }
    return badge;
  } else if (status === 'error') {
    const badge = <Badge content="Error" color="red" />;
    if (job.error_message) {
      return (
        <Whisper
          placement="topStart"
          trigger="hover"
          speaker={<Tooltip style={{ maxWidth: 480 }}>{job.error_message}</Tooltip>}
        >
          <span style={{ display: 'inline-block', cursor: 'default' }}>{badge}</span>
        </Whisper>
      );
    }
    return badge;
  } else if (status === 'killed') {
    return <Badge content="Killed" color="orange" />;
  } else if (status === 'creating') {
    return <Badge content="Creating" color="cyan" />;
  } else {
    return <Badge content={status} color="gray" />;
  }
};

function Jobs() {
  const auth = useAuth();
  
  const fetchJobs = async () => {
    // Don't try to fetch if Auth0 is still loading or user is not authenticated
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      throw new Error('Authentication required. Please wait for Auth0 to initialize.');
    }
    
    const userId = getUserId(auth.user);
    if (!userId) {
      throw new Error('User ID not found. Please set up your user ID first.');
    }
    
    const headers = await getAuthHeaders(auth.getAccessTokenSilently);
    const response = await fetch(getApiUrl(`/users/${userId}/jobs`), {
      method: "GET",
      headers: headers,
    });
    
    if (response.status === 401) {
      throw new Error('Unauthorized - Authentication required');
    }
    
    // Handle 428 Precondition Required - user needs to set user_id
    if (response.status === 428) {
      try {
        const errorData = await response.json();
        const errorMessage = errorData.detail?.message || errorData.detail?.error || errorData.message || 'User ID not set. Please set your user_id first.';
        throw new Error(errorMessage);
      } catch (parseError) {
        // If JSON parsing fails, use default message
        throw new Error('User ID not set. Please set your user_id first.');
      }
    }
    
    if (!response.ok) {
      // Try to parse error response for better error messages
      try {
        const errorData = await response.json();
        const errorMessage = errorData.detail?.message || errorData.detail?.error || errorData.message || `Failed to fetch jobs: ${response.statusText}`;
        throw new Error(errorMessage);
      } catch (parseError) {
        // If JSON parsing fails, use status text
        throw new Error(`Failed to fetch jobs: ${response.statusText}`);
      }
    }
    
    const data = await response.json();
    return data;
  };
  const [showKillModal, setShowKillModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobIdToKill, setJobIdToKill] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [killLoading, setKillLoading] = useState(false);
  const [killError, setKillError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState(null);
  const toaster = useToaster();

  // Trigger refresh when auth becomes ready
  useEffect(() => {
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently) {
      setRefreshKey(prev => prev + 1);
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.hasAuth0]);

  // Load jobs when refreshKey changes
  useEffect(() => {
    // Don't try to load if Auth0 is still loading or user is not authenticated
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      return;
    }
    
    const loadJobs = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchJobs();
        setJobs(data.jobs || []);
      } catch (err) {
        console.error('Error loading jobs:', err);
        setError(err.message);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    if (!auth.hasAuth0 || (auth.isAuthenticated && auth.getAccessTokenSilently)) {
      loadJobs();
    }
  }, [refreshKey, auth.isLoading, auth.isAuthenticated, auth.hasAuth0, auth.getAccessTokenSilently, auth.user]);

  const handleKill = async () => {
    if (!jobIdToKill) return;
    
    const userId = getUserId(auth.user);
    if (!userId) {
      setError('User ID not found. Please set up your user ID first.');
      return;
    }
    
    setKillLoading(true);
    setKillError(null);
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(getApiUrl(`/users/${userId}/jobs/${jobIdToKill}/kill`), {
        method: 'POST',
        headers: headers,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData?.detail?.message || errorData?.detail || errorData?.message || 'Failed to kill job';
        throw new Error(errorMessage);
      }
      
      await response.json();
      setShowKillModal(false);
      setJobIdToKill(null);
      setKillError(null);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error killing job:', error);
      setKillError(error.message || 'Failed to kill job. Please try again.');
    } finally {
      setKillLoading(false);
    }
  };

  const handleRemove = async (jobIdsToRemove) => {
    if (!jobIdsToRemove || jobIdsToRemove.length === 0) return;
    
    const userId = getUserId(auth.user);
    if (!userId) {
      setError('User ID not found. Please set up your user ID first.');
      return;
    }
    
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const results = await Promise.allSettled(
        jobIdsToRemove.map(jobId =>
          fetch(getApiUrl(`/users/${userId}/jobs/${jobId}`), {
            method: 'DELETE',
            headers: headers,
          })
        )
      );
      
      const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok));
      if (failed.length > 0) {
        console.error('Some jobs failed to remove:', failed);
      }
      
      setShowRemoveModal(false);
      setSelectedJobIds(new Set());
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error removing jobs:', error);
      setError('Failed to remove some jobs. Please try again.');
    }
  };

  const handleBulkRemove = () => {
    if (selectedJobIds.size === 0) return;
    setShowRemoveModal(true);
  };

  const handleToggleSelect = (jobId) => {
    setSelectedJobIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (jobs) => {
    const allSelected = jobs.every(job => selectedJobIds.has(job.job_id));
    if (allSelected) {
      // Deselect all filtered jobs
      const newSet = new Set(selectedJobIds);
      jobs.forEach(job => newSet.delete(job.job_id));
      setSelectedJobIds(newSet);
    } else {
      // Select all filtered jobs
      const newSet = new Set(selectedJobIds);
      jobs.forEach(job => newSet.add(job.job_id));
      setSelectedJobIds(newSet);
    }
  };

  const handleViewLogs = (job) => {
    setSelectedJob(job);
    setShowLogsModal(true);
  };

  const handleViewMetrics = (job) => {
    setSelectedJob(job);
    setShowMetricsModal(true);
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  // Download the source archive the user submitted for this job. The API
  // returns a presigned URL (1h); we hand it to the browser to download.
  const handleDownloadArchive = async (job) => {
    try {
      const userId = getUserId(auth.user);
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(
        getApiUrl(`/users/${userId}/jobs/${job.job_id}/archive`),
        { method: 'GET', headers }
      );
      if (response.status === 404) {
        toaster.push(
          <Message type="info" showIcon>
            No source archive stored for this job (git-repo job, or submitted before archive download was added).
          </Message>,
          { placement: 'bottomCenter', duration: 6000 }
        );
        return;
      }
      if (!response.ok) {
        throw new Error(`Failed to get archive URL (${response.status})`);
      }
      const data = await response.json();
      const a = document.createElement('a');
      a.href = data.download_url;
      a.download = data.filename || `${job.job_id}-source.tar.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error('Error downloading job archive:', err);
      toaster.push(
        <Message type="error" showIcon>
          {err.message || 'Failed to download archive'}
        </Message>,
        { placement: 'bottomCenter', duration: 6000 }
      );
    }
  };

  const canKillJob = (job) => {
    return job.status === 'running';
  };

  const canRemoveJob = (job) => {
    return job.status !== 'running';
  };

  const filteredJobs = (jobs) => {
    if (!searchTerm) return jobs;
    const term = searchTerm.toLowerCase();
    return jobs.filter(job => 
      job.job_id.toLowerCase().includes(term) ||
      (job.description && job.description.toLowerCase().includes(term)) ||
      (job.docker_image && job.docker_image.toLowerCase().includes(term))
    );
  };

  return (
    <Container>
      <Header style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
        <Navbar style={{ display: 'flex', alignItems: 'center' }}>
          <Navbar.Brand>
            All Jobs
          </Navbar.Brand>
          <div style={{ marginLeft: 'auto', marginRight: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <InputGroup inside style={{ width: 300 }}>
              <Input
                placeholder="Search by Job ID, Description, or Docker Image"
                value={searchTerm}
                onChange={setSearchTerm}
              />
              <InputGroup.Addon>
                <Search />
              </InputGroup.Addon>
            </InputGroup>
            {selectedJobIds.size > 0 && (
              <Button 
                appearance="primary" 
                color="red" 
                onClick={handleBulkRemove}
                icon={<Trash />}
              >
                Remove ({selectedJobIds.size})
              </Button>
            )}
            <Button appearance="primary" onClick={() => setRefreshKey(prev => prev + 1)} loading={loading && jobs.length === 0}>
              Refresh
            </Button>
          </div>
        </Navbar>
      </Header>
      <Content>
        <div style={{ padding: '20px' }}>
          <Panel>
            
            {error && (
              <Message type="error" closable onClose={() => setError(null)}>
                {error}
              </Message>
            )}
            {auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently) ? (
              <div>Loading...</div>
            ) : !getUserId(auth.user) ? (
              <Message type="warning">
                User ID not found. Please set up your user ID first.
              </Message>
            ) : (
              <>
                <Table
                  data={filteredJobs(jobs)}
                  autoHeight
                  rowHeight={60}
                  loading={loading}
                  onRowClick={(rowData) => {
                    setSelectedJob(rowData);
                  }}
                >
                      <Column width={50}>
                        <HeaderCell>
                          <Checkbox
                            checked={jobs.length > 0 && jobs.every(job => selectedJobIds.has(job.job_id))}
                            indeterminate={jobs.some(job => selectedJobIds.has(job.job_id)) && !jobs.every(job => selectedJobIds.has(job.job_id))}
                            onChange={() => handleSelectAll(jobs)}
                          />
                        </HeaderCell>
                        <Cell>
                          {(rowData) => (
                            <Checkbox
                              checked={selectedJobIds.has(rowData.job_id)}
                              onChange={() => handleToggleSelect(rowData.job_id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </Cell>
                      </Column>
                      <Column width={120}>
                        <HeaderCell>Status</HeaderCell>
                        <Cell>
                          {(rowData) => getJobStatusBadge(rowData)}
                        </Cell>
                      </Column>
                      <Column flexGrow={1}>
                        <HeaderCell>Job ID</HeaderCell>
                        <Cell>
                          {(rowData) => (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{rowData.job_id}</span>
                              <div onClick={(e) => e.stopPropagation()}>
                                <CopyButton
                                  text={rowData.job_id}
                                  size="sm"
                                  successMessage="Job ID copied to clipboard"
                                  errorMessage="Failed to copy job ID to clipboard"
                                />
                              </div>
                            </div>
                          )}
                        </Cell>
                      </Column>
                      <Column width={150}>
                        <HeaderCell>Instance Type</HeaderCell>
                        <Cell>
                          {(rowData) => rowData.instance_type || '-'}
                        </Cell>
                      </Column>
                      <Column width={180}>
                        <HeaderCell>Created At</HeaderCell>
                        <Cell>
                          {(rowData) => <DateDisplay isoString={rowData.created_at} />}
                        </Cell>
                      </Column>
                      <Column width={100}>
                        <HeaderCell>Uptime</HeaderCell>
                        <Cell>
                          {(rowData) => formatUptime(rowData.uptime_seconds)}
                        </Cell>
                      </Column>
                      <Column width={100}>
                        <HeaderCell>Cost</HeaderCell>
                        <Cell>
                          {(rowData) => rowData.cost !== null && rowData.cost !== undefined ? `$${rowData.cost.toFixed(2)}` : '-'}
                        </Cell>
                      </Column>
                      <Column flexGrow={1} minWidth={150}>
                        <HeaderCell>Description</HeaderCell>
                        <Cell>
                          {(rowData) => {
                            const description = rowData.description;
                            if (!description) {
                              return '-';
                            }
                            const truncated = (
                              <div
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {description}
                              </div>
                            );
                            return (
                              <Whisper
                                placement="topStart"
                                trigger="hover"
                                speaker={<Tooltip style={{ maxWidth: 480 }}>{description}</Tooltip>}
                              >
                                {truncated}
                              </Whisper>
                            );
                          }}
                        </Cell>
                      </Column>
                      <Column flexGrow={1} minWidth={100} fixed="right">
                        <HeaderCell>Actions</HeaderCell>
                        <Cell>
                          {(rowData) => (
                            <ButtonToolbar style={{ whiteSpace: 'nowrap' }}>
                                <Whisper
                                  placement="top"
                                  trigger="hover"
                                  speaker={<Tooltip>View details</Tooltip>}
                                >
                                  <IconButton
                                    icon={<EditIcon />}
                                    color="blue"
                                    appearance="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewDetails(rowData);
                                    }}
                                  />
                                </Whisper>
                                <Whisper
                                  placement="top"
                                  trigger="hover"
                                  speaker={<Tooltip>View logs</Tooltip>}
                                >
                                  <IconButton
                                    icon={<Page />}
                                    color="yellow"
                                    appearance="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewLogs(rowData);
                                    }}
                                  />
                                </Whisper>
                                <Whisper
                                  placement="top"
                                  trigger="hover"
                                  speaker={<Tooltip>View GPU metrics</Tooltip>}
                                >
                                  <IconButton
                                    icon={<BarChart />}
                                    color="green"
                                    appearance="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewMetrics(rowData);
                                    }}
                                  />
                                </Whisper>
                                <Whisper
                                  placement="top"
                                  trigger="hover"
                                  speaker={<Tooltip>Download source archive</Tooltip>}
                                >
                                  <IconButton
                                    icon={<FileDownload />}
                                    color="cyan"
                                    appearance="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadArchive(rowData);
                                    }}
                                  />
                                </Whisper>
                                {canKillJob(rowData) && (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={<Tooltip>Kill job</Tooltip>}
                                  >
                                    <IconButton
                                      icon={<WarningRound />}
                                      color="red"
                                      appearance="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedJob(rowData);
                                        setJobIdToKill(rowData.job_id);
                                        setKillError(null);
                                        setShowKillModal(true);
                                      }}
                                    />
                                  </Whisper>
                                )}
                                {canRemoveJob(rowData) && (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={<Tooltip>Remove job</Tooltip>}
                                  >
                                    <IconButton
                                      icon={<Trash />}
                                      color="red"
                                      appearance="ghost"
                                      size="sm"

                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedJob(rowData);
                                        setShowRemoveModal(true);
                                      }}
                                    />
                                  </Whisper>
                                )}
                            </ButtonToolbar>
                          )}
                        </Cell>
                      </Column>
                    </Table>

                <Modal 
                      open={showKillModal} 
                      onClose={() => {
                        setShowKillModal(false);
                        setJobIdToKill(null);
                        setKillError(null);
                      }}
                      backdrop="static"
                      keyboard={false}
                    >
                      <Modal.Header>
                        <Modal.Title>Confirm Kill</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        Are you sure you want to kill job {jobIdToKill}?
                        {killError && (
                          <Message type="error" style={{ marginTop: '10px' }}>
                            {killError}
                          </Message>
                        )}
                      </Modal.Body>
                      <Modal.Footer>
                        <Button
                          onClick={() => {
                            setShowKillModal(false);
                            setJobIdToKill(null);
                            setKillError(null);
                          }}
                          appearance="subtle"
                          disabled={killLoading}
                        >
                          Cancel
                        </Button>
                        <Button onClick={handleKill} appearance="primary" color="red" loading={killLoading} disabled={!jobIdToKill}>
                          Kill
                        </Button>
                      </Modal.Footer>
                    </Modal>

                    <Modal 
                      open={showRemoveModal} 
                      onClose={() => setShowRemoveModal(false)}
                      backdrop="static"
                      keyboard={false}
                    >
                      <Modal.Header>
                        <Modal.Title>Confirm Remove</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        {selectedJobIds.size > 0 ? (
                          <>
                            Are you sure you want to remove {selectedJobIds.size} selected job{selectedJobIds.size !== 1 ? 's' : ''}? This will delete all state and logs.
                            {Array.from(selectedJobIds).some(id => {
                              const job = jobs.find(j => j.job_id === id);
                              return job && job.status === 'running';
                            }) && (
                              <Message type="warning" style={{ marginTop: '10px' }}>
                                Note: Running jobs cannot be removed. They will be skipped.
                              </Message>
                            )}
                          </>
                        ) : (
                          <>Are you sure you want to remove job {selectedJob?.job_id}? This will delete all state and logs.</>
                        )}
                      </Modal.Body>
                      <Modal.Footer>
                        <Button onClick={() => setShowRemoveModal(false)} appearance="subtle">
                          Cancel
                        </Button>
                        <Button 
                          onClick={() => {
                            if (selectedJobIds.size > 0) {
                              const removableJobIds = Array.from(selectedJobIds).filter(id => {
                                const job = jobs.find(j => j.job_id === id);
                                return job && canRemoveJob(job);
                              });
                              handleRemove(removableJobIds);
                            } else if (selectedJob) {
                              handleRemove([selectedJob.job_id]);
                            }
                          }} 
                          appearance="primary" 
                          color="red"
                        >
                          Remove
                        </Button>
                      </Modal.Footer>
                    </Modal>

                    <LogDialog 
                      show={showLogsModal}
                      onClose={() => setShowLogsModal(false)}
                      jobId={selectedJob?.job_id}
                    />

                    <MetricsDialog
                      show={showMetricsModal}
                      onClose={() => setShowMetricsModal(false)}
                      jobId={selectedJob?.job_id}
                    />

                <JobDetailsDialog 
                  show={showDetailsModal}
                  onClose={() => setShowDetailsModal(false)}
                  jobId={selectedJob?.job_id}
                />
              </>
            )}
          </Panel>
        </div>
      </Content>
    </Container>
  );
}

export default Jobs;